import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import DataTable from 'src/components/DataTable';
import PageLayout from 'src/components/PageLayout';
import ConfirmationDialog from 'src/components/ConfirmationDialog';

import {
  useGetTutorial,
  useGetTutorialQuestions,
  useDeleteTutorialQuestion,
} from './api';
import messages from './messages';
import TutorialQuestionForm from './components/TutorialQuestionForm';

const textWrapSX = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
};

export default function TutorialDetailsPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const { formatMessage } = useIntl();

  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });

  const { data: questions, isFetching: isFetchingQuestions } =
    useGetTutorialQuestions({
      params,
      uuid: uuid ?? '',
      config: { enabled: !!uuid },
    });
  const { data: tutorial } = useGetTutorial(uuid ?? '', {
    enabled: !!uuid,
  });
  const { mutate: deleteTutorialQuestion } = useDeleteTutorialQuestion();

  const formatBooleanField = useCallback((value: boolean) => {
    return value
      ? formatMessage(messages.yesLabel)
      : formatMessage(messages.noLabel);
  }, []);

  const handleDelete = useCallback(
    (questionUuid: string) => {
      if (uuid) {
        deleteTutorialQuestion({
          tutorialUuid: uuid,
          questionUuid,
        });
      }
    },
    [uuid],
  );

  const columns: Array<{
    id: keyof UCM.TutorialQuestionType | string;
    label: string | React.ReactNode;
  }> = useMemo(
    () => [
      {
        id: 'code',
        label: formatMessage(messages.codeColumnLabel),
        render: (code: string) => {
          return <Typography variant="inherit">{code}</Typography>;
        },
      },
      {
        id: 'questionEn',
        label: formatMessage(messages.questionEnColumnLabel),
        render: (_: Object, item: UCM.TutorialQuestionType) => {
          return <Typography variant="inherit">{item.question.en}</Typography>;
        },
      },
      {
        id: 'questionDe',
        label: formatMessage(messages.questionDeColumnLabel),
        render: (_: Object, item: UCM.TutorialQuestionType) => {
          return <Typography variant="inherit">{item.question.de}</Typography>;
        },
      },
      {
        id: 'options',
        label: formatMessage(messages.optionsColumnLabel),
        align: 'center',
        render: (options: Array<UCM.QuestionOptionType>) => {
          return <Typography variant="inherit">{options.length}</Typography>;
        },
      },
      {
        id: 'isVisible',
        label: formatMessage(messages.visibleColumnLabel),
        align: 'center',
        render: (visible: boolean) => {
          return (
            <Typography variant="inherit">
              {formatBooleanField(visible)}
            </Typography>
          );
        },
      },
      {
        id: 'actions',
        label: '',
        align: 'center',
        width: 0,
        render: (_: Object, question: UCM.TutorialQuestionType) => (
          <Grid display="flex" alignItems="center">
            <TutorialQuestionForm create={false} tutorialQuestion={question} />

            <ConfirmationDialog
              onConfirm={() => handleDelete(question.uuid)}
              message={formatMessage(messages.deleteTutorialConfirmation)}
            >
              {(handleOpen) => (
                <Tooltip arrow title={formatMessage(messages.deleteTooltip)}>
                  <IconButton onClick={handleOpen}>
                    <DeleteForeverOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </ConfirmationDialog>
          </Grid>
        ),
      },
    ],
    [handleDelete],
  );

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  return (
    <PageLayout backHref="/setup">
      {tutorial && (
        <Grid container spacing={2}>
          <Typography variant="h4">
            {formatMessage(messages.tutorialDetailsSectionTitle)}
          </Typography>

          <Grid container size={12} component={Paper} px={1} py={2} spacing={1}>
            <DetailItem
              value={tutorial.code}
              label={formatMessage(messages.codeLabel)}
            />
            <DetailItem
              value={tutorial.title.en}
              label={formatMessage(messages.titleEnLabel)}
            />
            <DetailItem
              value={tutorial.title.de}
              label={formatMessage(messages.titleDeLabel)}
            />
            <DetailItem
              value={tutorial.kind.name ?? '-'}
              label={formatMessage(messages.kindLabel)}
            />
            <DetailItem
              value={tutorial.cohort.name ?? '-'}
              label={formatMessage(messages.cohortLabel)}
            />
          </Grid>
        </Grid>
      )}

      <Grid container size={12} mt={5} spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="h4">
            {formatMessage(messages.tutorialQuestionsSectionTitle)}
          </Typography>
        </Grid>

        <Grid
          container
          alignItems="center"
          size={{ xs: 12, sm: 6 }}
          justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
        >
          <TutorialQuestionForm create={true} />
        </Grid>

        <Grid container size={12}>
          <DataTable
            columns={columns}
            rows={questions?.data}
            loading={isFetchingQuestions}
            pagination={questions?.meta}
            onPaginate={handlePagination}
          />
        </Grid>
      </Grid>
    </PageLayout>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <Grid
      size={{ xs: 12, sm: 6, md: 4 }}
      container
      spacing={1}
      display="flex"
      flexDirection="row"
    >
      <Typography variant="body1" color="textSecondary">
        {label}
      </Typography>
      <Typography
        sx={textWrapSX}
        variant="body1"
        color="primary"
        fontWeight="bold"
      >
        {value}
      </Typography>
    </Grid>
  );
}
